import { useCallback, useEffect, useRef } from 'react';

/**
 * A hook that implements debouncing for back navigation to prevent
 * multiple rapid back button clicks from causing navigation issues
 *
 * @param onNavigate Function to execute when back navigation is allowed
 * @returns Object containing the handleBack function and a cleanup function
 */
export const useBackNavigationDebounce = (onNavigate: () => Promise<void> | void) => {
    const isProcessingBack = useRef(false);
    const lastBackClickTime = useRef(0);
    const backTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const MIN_CLICK_INTERVAL = 1000; // 1 second between allowed clicks

    const handleBack = useCallback(async () => {
        const now = Date.now();
        const timeSinceLastClick = now - lastBackClickTime.current;

        // Prevent multiple clicks within the minimum interval
        if (isProcessingBack.current || timeSinceLastClick < MIN_CLICK_INTERVAL) {
            return false; // Indicates the click was ignored
        }

        // Update the timestamp of the last click
        lastBackClickTime.current = now;

        // Set processing flag to prevent concurrent navigation
        isProcessingBack.current = true;

        try {
            // Clean up any existing timeout
            if (backTimeoutRef.current) {
                clearTimeout(backTimeoutRef.current);
            }

            // Perform the actual navigation
            await onNavigate();
            return true; // Indicates navigation was processed
        } finally {
            // Reset the processing flag after navigation completes and after delay
            backTimeoutRef.current = setTimeout(() => {
                isProcessingBack.current = false;
            }, MIN_CLICK_INTERVAL);
        }
    }, [onNavigate]);

    // Clean up timeout when component unmounts
    useEffect(() => {
        return () => {
            if (backTimeoutRef.current) {
                clearTimeout(backTimeoutRef.current);
            }
        };
    }, []);

    return { handleBack };
};
